import { useEffect, useRef } from 'react'

/**
 * A custom hook that runs a callback at a given interval,
 * but only when the page is visible (document.visibilityState === 'visible').
 * Equivalent to RTK Query's skipPollingIfUnfocused behavior.
 *
 * Ticks are skipped while the previous run is still in flight: if `callback`
 * returns a promise the hook awaits it, and `isBusy` lets callers report work
 * the promise doesn't cover (e.g. a grid cache refresh that fetches later).
 *
 * @param callback - The function to execute on each interval tick
 * @param delay - Interval duration in milliseconds (pass `null` to pause)
 * @param isBusy - Optional predicate; when it returns true the tick is skipped
 */
export function useVisibilityAwareInterval(
  callback: () => void | Promise<void>,
  delay: number | null,
  isBusy?: () => boolean
): void {
  const callbackRef = useRef(callback)
  const isBusyRef = useRef(isBusy)
  const isRunningRef = useRef(false)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    isBusyRef.current = isBusy
  }, [isBusy])

  useEffect(() => {
    if (delay === null || isNaN(delay)) return

    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (isRunningRef.current || isBusyRef.current?.()) return

      isRunningRef.current = true
      void Promise.resolve(callbackRef.current()).finally(() => {
        isRunningRef.current = false
      })
    }, delay)

    return () => {
      clearInterval(interval)
      isRunningRef.current = false
    }
  }, [delay])
}
